import { stableRecordKey } from '../lib/rowIdentity';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Upload, Camera, FileText, Image as ImageIcon, X, CheckCircle, AlertTriangle, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * Global reusable secure document-upload component (Membership ID, Saturday Club, photos, future forms).
 * - single or multiple files (maxFiles)
 * - choose existing file / photo, or take a photo on compatible mobile devices
 * - client-side extension + size pre-checks (server performs the authoritative MIME/content validation)
 * - preview (images), remove, replace
 *
 * Props:
 *   files: File[]                 controlled list of selected files
 *   onChange(files: File[])       called with the updated list
 *   maxFiles = 1                  1 = single-file mode
 *   maxSizeMb = 30                client pre-check only
 *   accept = 'documents+images'   preset: 'documents+images' | 'images' | 'documents+images+spreadsheets'
 *   label, hint, required, testId, disabled
 */
const PRESETS = {
  'documents+images': {
    exts: ['pdf', 'doc', 'docx', 'odt', 'rtf', 'txt', 'jpg', 'jpeg', 'png', 'heic', 'heif', 'webp', 'tif', 'tiff'],
    label: 'PDF, Word, ODT, RTF, TXT, JPG, JPEG, PNG, HEIC, WebP, TIFF',
  },
  images: {
    exts: ['jpg', 'jpeg', 'png', 'heic', 'heif', 'webp', 'tif', 'tiff'],
    label: 'JPG, JPEG, PNG, HEIC, WebP, TIFF',
  },
  'documents+images+spreadsheets': {
    exts: ['pdf', 'doc', 'docx', 'odt', 'rtf', 'txt', 'jpg', 'jpeg', 'png', 'heic', 'heif', 'webp', 'tif', 'tiff', 'xls', 'xlsx', 'csv'],
    label: 'PDF, Word, ODT, RTF, TXT, JPG, JPEG, PNG, HEIC, WebP, TIFF, XLS, XLSX, CSV',
  },
};
const BLOCKED = ['exe', 'bat', 'cmd', 'com', 'js', 'vbs', 'ps1', 'sh', 'php', 'apk', 'dll', 'scr', 'msi', 'jar', 'zip', 'rar', '7z', 'html', 'htm', 'svg'];

const extOf = (name = '') => (name.includes('.') ? name.split('.').pop().toLowerCase() : '');
const isImageFile = (f) => /^image\//.test(f.type) || ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'tif', 'tiff'].includes(extOf(f.name));
const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export const DocumentUpload = ({
  files = [], onChange, maxFiles = 1, maxSizeMb = 30, accept = 'documents+images',
  label = 'Upload Document', hint = '', required = false, testId = 'document-upload', disabled = false,
}) => {
  const fileInput = useRef(null);
  const cameraInput = useRef(null);
  const [previews, setPreviews] = useState({});
  const [previewOpen, setPreviewOpen] = useState(null);
  const preset = PRESETS[accept] || PRESETS['documents+images'];
  const single = maxFiles <= 1;
  const canTakePhoto = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');

  useEffect(() => {
    // build object URLs for image previews (browser-displayable types only)
    const next = {};
    files.forEach((f, i) => {
      if (isImageFile(f) && !['heic', 'heif', 'tif', 'tiff'].includes(extOf(f.name))) next[i] = URL.createObjectURL(f);
    });
    setPreviews(next);
    return () => Object.values(next).forEach((u) => URL.revokeObjectURL(u));
  }, [files]);

  const validate = useCallback((f) => {
    const ext = extOf(f.name);
    if (!ext) return 'File must have an extension (e.g. .pdf, .jpg)';
    if (BLOCKED.includes(ext)) return `.${ext} files are not permitted for security reasons`;
    if (!preset.exts.includes(ext)) return `Unsupported file type .${ext}. Accepted: ${preset.label}`;
    if (f.size > maxSizeMb * 1024 * 1024) return `"${f.name}" is too large. Maximum size is ${maxSizeMb}MB`;
    if (f.size < 16) return `"${f.name}" appears to be empty or corrupted`;
    return null;
  }, [preset, maxSizeMb]);

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    const valid = [];
    for (const f of incoming) {
      const err = validate(f);
      if (err) { toast.error(err); continue; }
      valid.push(f);
    }
    if (!valid.length) return;
    let next = single ? [valid[0]] : [...files, ...valid];
    if (!single && next.length > maxFiles) {
      toast.error(`You can upload a maximum of ${maxFiles} files`);
      next = next.slice(0, maxFiles);
    }
    onChange(next);
    toast.success(single ? `${valid[0].name} ready to upload` : `${valid.length} file(s) added`);
  };

  const removeAt = (idx) => onChange(files.filter((_, i) => i !== idx));

  const onInput = (e) => { addFiles(e.target.files); e.target.value = ''; };
  const onDrop = (e) => { e.preventDefault(); if (!disabled) addFiles(e.dataTransfer.files); };

  return (
    <div className="space-y-3" data-testid={testId}>
      {label && (
        <div className="text-sm font-semibold text-[hsl(var(--lux-royal,var(--gs-navy)))]">
          {label}{required && <span className="text-[hsl(var(--destructive))]"> *</span>}
        </div>
      )}
      {hint && <p className="text-xs text-[hsl(var(--muted-foreground))]">{hint}</p>}

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className={`rounded-xl border-2 border-dashed p-4 transition-colors ${disabled ? 'opacity-60' : 'hover:border-[hsl(var(--lux-gold,var(--gs-saffron)))]'} border-[hsl(var(--lux-gold,var(--gs-saffron))/0.5)] bg-white/60`}
        data-testid={`${testId}-dropzone`}
      >
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <Button type="button" variant="outline" disabled={disabled || (!single && files.length >= maxFiles)}
            onClick={() => fileInput.current?.click()} className="justify-center" data-testid={`${testId}-choose-btn`}>
            <Upload className="w-4 h-4 mr-2" /> {single && files.length ? 'Replace File' : 'Choose File'}
          </Button>
          {canTakePhoto && (
            <Button type="button" variant="outline" disabled={disabled || (!single && files.length >= maxFiles)}
              onClick={() => cameraInput.current?.click()} className="justify-center" data-testid={`${testId}-camera-btn`}>
              <Camera className="w-4 h-4 mr-2" /> Take Photo
            </Button>
          )}
          <span className="text-xs text-[hsl(var(--muted-foreground))] sm:ml-auto">
            Accepted: {preset.label}. Max {maxSizeMb}MB{!single ? ` · up to ${maxFiles} files` : ''}.
          </span>
        </div>
        <input ref={fileInput} type="file" className="hidden" multiple={!single}
          accept={preset.exts.map((e) => `.${e}`).join(',')} onChange={onInput} data-testid={`${testId}-input`} />
        <input ref={cameraInput} type="file" className="hidden" accept="image/*" capture="environment" onChange={onInput}
          data-testid={`${testId}-camera-input`} />
      </div>

      {files.length > 0 && (
        <ul className="space-y-2" data-testid={`${testId}-list`}>
          {files.map((f, i) => (
            <li key={stableRecordKey(f)} className="flex items-center gap-3 p-3 rounded-lg border border-[hsl(var(--lux-gold,var(--gs-saffron))/0.35)] bg-[hsl(var(--lux-royal-soft,var(--muted)))]"
              data-testid={`${testId}-item-${i}`}>
              {previews[i] ? (
                <img src={previews[i]} alt="" className="w-12 h-12 rounded object-cover border" />
              ) : (
                <div className="w-12 h-12 rounded bg-white flex items-center justify-center border">
                  {isImageFile(f) ? <ImageIcon className="w-5 h-5 text-[hsl(var(--lux-royal,var(--gs-navy)))]" /> : <FileText className="w-5 h-5 text-[hsl(var(--lux-royal,var(--gs-navy)))]" />}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-[hsl(var(--lux-gold,var(--gs-saffron)))] shrink-0" />
                  <span className="truncate">{f.name}</span>
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{fmtSize(f.size)} · ready to upload</div>
              </div>
              {previews[i] && (
                <Button type="button" size="icon" variant="ghost" onClick={() => setPreviewOpen(previews[i])} aria-label="Preview" data-testid={`${testId}-preview-${i}`}>
                  <Eye className="w-4 h-4" />
                </Button>
              )}
              <Button type="button" size="icon" variant="ghost" onClick={() => removeAt(i)} aria-label="Remove file" data-testid={`${testId}-remove-${i}`}>
                <X className="w-4 h-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {required && files.length === 0 && (
        <p className="text-xs flex items-center gap-1 text-[hsl(var(--muted-foreground))]"><AlertTriangle className="w-3 h-3" /> A file is required.</p>
      )}

      {previewOpen && (
        <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreviewOpen(null)} data-testid={`${testId}-preview-modal`}>
          <img src={previewOpen} alt="Preview" className="max-h-[85vh] max-w-full rounded-xl shadow-2xl" />
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
